/**
 * The deck validator shared by runebound.gg (web and app) and Runebound Play. It checks, with citations:
 *
 * | Check | Rule |
 * |---|---|
 * | a Champion Legend | CR 103.2 |
 * | a Chosen Champion: a champion unit with the legend's champion tag | CR 103.3.a, 103.3.a.2 |
 * | exactly 40 Main Deck cards including the champion | TR 402.1 (CR 103.3: at least 40) |
 * | Main Deck and sideboard hold units, spells and gear only | CR 103.3, TR 601.1.c.2 |
 * | domain identity: the legend's domains; Colorless cards fit any deck | CR 103.2.b.3-4 |
 * | at most 3 copies of a name across champion, Main Deck and sideboard (card text can lift it) | CR 103.3.b, TR 403.3, CR 002 |
 * | at most 1 copy of a `[Unique]` card's name | CR 825.3.a |
 * | at most 3 Signature cards, all with the legend's champion tag | CR 103.3.d.1-103.3.d.2, 825.3.b |
 * | exactly 12 runes of the legend's domains | CR 103.4.a, 103.4.a.1 |
 * | exactly 3 battlefields with distinct names | CR 103.5.a, 103.5.c, 487.4.a, TR 402.1 |
 * | sideboard of at most 10 | TR 601.1.c.1 |
 * | no banned card in any zone (live list, static fallback) | TR 601.2.d |
 * | every card legal in the format (by name, from a legal set) | TR 601.2.a, 601.3 |
 *
 * Copy and Signature limits count the sideboard too (TR 403.3, 601.1.c.3): otherwise sideboarding could
 * push a deck over them. The verdict is `ok`: no issues at all; a banned card makes a deck not legal.
 */
import { type BanList, resolveBanList } from './bans';
import {
  cardLabel,
  championTag,
  fitsIdentity,
  hasTag,
  isChampionUnit,
  isLegalInFormat,
  isLegend,
  isMainDeckCard,
  isSignature,
  isUnique,
  legendDomains,
  liftsCopyLimit,
} from './cards';
import { FORMATS, type FormatId } from './formats';
import { type DeckIssue, type DeckSection, issue } from './issues';
import { cardNameKey, normaliseCardName } from './names';
import type { DeckRulesCard, DeckRulesDeck, DeckRulesEntry, DeckRulesLookup, DeckRulesSlot } from './types';

/** Format and live ban list for `validateDeck`. */
export interface ValidateOptions {
  /** `standard` when omitted. */
  format?: FormatId | undefined;
  /**
   * The live ban list (card names, e.g. Runebound `meta_card.banned`). Missing or empty: the static
   * fallback applies. A format's extra bans (2v2) always apply on top.
   */
  bannedNames?: Iterable<string> | null | undefined;
}

/** Section sizes of a deck as the validator counted them. */
export interface DeckCounts {
  /** Main Deck cards including the Chosen Champion. */
  main: number;
  runes: number;
  battlefields: number;
  sideboard: number;
  signatures: number;
}

/** The validator verdict: `ok` when there are no issues. */
export interface DeckValidation {
  ok: boolean;
  issues: DeckIssue[];
  counts: DeckCounts;
  banList: BanList['source'];
}

function total(entries: readonly DeckRulesEntry[]): number {
  return entries.reduce((n, e) => n + e.quantity, 0);
}

/** Validates a deck against a format. */
export function validateDeck(
  deck: DeckRulesDeck,
  lookup: DeckRulesLookup,
  options: ValidateOptions = {},
): DeckValidation {
  const format = FORMATS[options.format ?? 'standard'];
  const bans = resolveBanList(options.bannedNames);
  const banned = new Set(bans.names);
  for (const n of format.extraBans) banned.add(normaliseCardName(n));
  const issues: DeckIssue[] = [];
  const counts: DeckCounts = {
    main: total(deck.main) + (deck.champion ? 1 : 0),
    runes: total(deck.runes),
    battlefields: total(deck.battlefields),
    sideboard: total(deck.sideboard),
    signatures: 0,
  };

  const resolve = (entry: DeckRulesSlot, section: DeckSection): DeckRulesCard | null => {
    const card = lookup(entry.cardId);
    if (!card) issues.push(issue('unknownCard', 'catalogue', `Unknown card ${entry.cardId}`, entry.cardId, section));
    return card ?? null;
  };
  const name = cardLabel;
  const checkCommon = (card: DeckRulesCard, section: DeckSection) => {
    if (banned.has(normaliseCardName(card.name)) || banned.has(cardNameKey(card.name)))
      issues.push(issue('banned', 'TR 601.2.d', `${name(card)} is banned in ${format.name}`, card.id, section));
    if (!isLegalInFormat(card, format))
      issues.push(
        issue('notLegal', 'TR 601.2.a, 601.3', `${name(card)} is not legal in ${format.name}`, card.id, section),
      );
  };
  for (const [section, entries] of [
    ['main', deck.main],
    ['runes', deck.runes],
    ['battlefields', deck.battlefields],
    ['sideboard', deck.sideboard],
  ] as const) {
    for (const e of entries) {
      if (!Number.isInteger(e.quantity) || e.quantity < 1) {
        issues.push(
          issue('quantity', 'deck', `${e.cardId}: quantity must be a positive whole number`, e.cardId, section),
        );
      }
    }
  }

  // Champion Legend (CR 103.2) and its identity.
  const legend = deck.legend?.cardId ? resolve(deck.legend, 'legend') : null;
  if (!deck.legend?.cardId) issues.push(issue('legend', 'CR 103.2', 'Choose a Champion Legend', null, 'legend'));
  else if (legend && !isLegend(legend))
    issues.push(issue('legend', 'CR 103.2', `${name(legend)} is not a legend`, legend.id, 'legend'));
  const legendOk = legend !== null && isLegend(legend);
  const identity = legendOk ? legendDomains(legend) : null;
  const tag = legendOk ? championTag(legend) : null;
  if (legend) checkCommon(legend, 'legend');

  // Chosen Champion (CR 103.3.a).
  const champion = deck.champion?.cardId ? resolve(deck.champion, 'champion') : null;
  if (!deck.champion?.cardId)
    issues.push(issue('champion', 'CR 103.3.a', 'Choose your Chosen Champion', null, 'champion'));
  else if (champion) {
    if (!isChampionUnit(champion)) {
      issues.push(
        issue('champion', 'CR 103.3.a.2', `${name(champion)} is not a champion unit`, champion.id, 'champion'),
      );
    } else if (tag && !hasTag(champion, tag)) {
      issues.push(
        issue(
          'champion',
          'CR 103.3.a.2',
          `${name(champion)} is not one of ${legend ? name(legend) : 'the legend'}'s champions`,
          champion.id,
          'champion',
        ),
      );
    }
    checkCommon(champion, 'champion');
  }

  // Main Deck size (TR 402.1).
  if (counts.main !== format.mainSize) {
    issues.push(
      issue(
        'mainSize',
        'TR 402.1',
        `The Main Deck has ${counts.main} of ${format.mainSize} cards (Chosen Champion included)`,
        null,
        'main',
      ),
    );
  }

  // Main Deck + sideboard + champion: types, identity, copies, Unique, Signatures.
  const copies = new Map<string, { n: number; card: DeckRulesCard; unlimited: boolean; unique: boolean }>();
  const count = (card: DeckRulesCard, n: number) => {
    const key = cardNameKey(card.name);
    const prev = copies.get(key);
    copies.set(key, {
      n: (prev?.n ?? 0) + n,
      card: prev?.card ?? card,
      unlimited: (prev?.unlimited ?? false) || liftsCopyLimit(card),
      unique: (prev?.unique ?? false) || isUnique(card),
    });
  };
  if (champion) count(champion, 1);
  if (champion && identity && !fitsIdentity(champion, identity)) {
    issues.push(
      issue(
        'domain',
        'CR 103.2.b.3-4',
        `${name(champion)} is outside the legend's domains (${identity.join(' / ')})`,
        champion.id,
        'champion',
      ),
    );
  }
  for (const [section, entries] of [
    ['main', deck.main],
    ['sideboard', deck.sideboard],
  ] as const) {
    for (const entry of entries) {
      const card = resolve(entry, section);
      if (!card) continue;
      checkCommon(card, section);
      if (!isMainDeckCard(card)) {
        issues.push(
          issue(
            'cardType',
            'CR 103.3',
            `${name(card)} (${card.type}) can't be in the ${section === 'main' ? 'Main Deck' : 'sideboard'}`,
            card.id,
            section,
          ),
        );
      }
      if (identity && !fitsIdentity(card, identity)) {
        issues.push(
          issue(
            'domain',
            'CR 103.2.b.3-4',
            `${name(card)} is outside the legend's domains (${identity.join(' / ')})`,
            card.id,
            section,
          ),
        );
      }
      if (isSignature(card)) {
        counts.signatures += entry.quantity;
        if (tag && !hasTag(card, tag)) {
          issues.push(
            issue(
              'signatureTag',
              'CR 103.3.d.2',
              `${name(card)} is a Signature card of another champion`,
              card.id,
              section,
            ),
          );
        }
      }
      count(card, entry.quantity);
    }
  }
  for (const { n, card, unlimited, unique } of copies.values()) {
    if (unique && n > 1) {
      issues.push(issue('unique', 'CR 825.3.a', `${name(card)} is Unique: at most 1 copy (${n})`, card.id, null));
    } else if (!unlimited && n > format.copyLimit) {
      issues.push(
        issue(
          'copies',
          'CR 103.3.b, TR 403.3',
          `${name(card)}: ${n} copies (at most ${format.copyLimit})`,
          card.id,
          null,
        ),
      );
    }
  }
  if (counts.signatures > format.signatureLimit) {
    issues.push(
      issue(
        'signatureCount',
        'CR 103.3.d.1',
        `${counts.signatures} Signature cards (at most ${format.signatureLimit})`,
        null,
        null,
      ),
    );
  }

  // Rune Deck (CR 103.4).
  if (counts.runes !== format.runeCount) {
    issues.push(
      issue('runeCount', 'CR 103.4.a', `The Rune Deck has ${counts.runes} of ${format.runeCount} runes`, null, 'runes'),
    );
  }
  for (const entry of deck.runes) {
    const card = resolve(entry, 'runes');
    if (!card) continue;
    checkCommon(card, 'runes');
    if (card.type !== 'Rune')
      issues.push(issue('runeType', 'CR 103.4', `${name(card)} is not a rune`, card.id, 'runes'));
    else if (identity && !fitsIdentity(card, identity)) {
      issues.push(
        issue(
          'runeDomain',
          'CR 103.4.a.1',
          `${name(card)} is outside the legend's domains (${identity.join(' / ')})`,
          card.id,
          'runes',
        ),
      );
    }
  }

  // Battlefields (CR 103.5).
  if (counts.battlefields !== format.battlefieldCount) {
    issues.push(
      issue(
        'battlefieldCount',
        'CR 103.5.a, TR 402.1',
        `${counts.battlefields} of ${format.battlefieldCount} battlefields`,
        null,
        'battlefields',
      ),
    );
  }
  const seen = new Set<string>();
  for (const entry of deck.battlefields) {
    const card = resolve(entry, 'battlefields');
    if (!card) continue;
    checkCommon(card, 'battlefields');
    if (card.type !== 'Battlefield')
      issues.push(issue('battlefieldType', 'CR 103.5', `${name(card)} is not a battlefield`, card.id, 'battlefields'));
    const key = cardNameKey(card.name);
    if (entry.quantity > 1 || seen.has(key)) {
      issues.push(
        issue('battlefieldDuplicate', 'CR 103.5.c', `${name(card)} appears more than once`, card.id, 'battlefields'),
      );
    }
    seen.add(key);
  }

  // Sideboard (TR 601.1.c.1).
  if (counts.sideboard > format.sideboardMax) {
    issues.push(
      issue(
        'sideboardSize',
        'TR 601.1.c.1',
        `The sideboard has ${counts.sideboard} cards (at most ${format.sideboardMax})`,
        null,
        'sideboard',
      ),
    );
  }

  return { ok: issues.length === 0, issues, counts, banList: bans.source };
}
